import { Button, Checkbox, Col, Form, Modal, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import PrilohyTable from 'components/molecules/Dokumenty/PrilohyTable';
import FormWrapper from 'components/molecules/FormWrapper';
import { getCountryText } from 'components/pages/Lei/formularPages/PrehledPages/DetailInfoPrehled';

import { useJinaOsobaIsins } from 'hooks/dokumenty/useJinaOsobaIsins';
import useProfily from 'hooks/useProfily';

import { EZpusobPrevzati, IVypisJina, PrikazVypisJinyGet, PrikazVypisJinyPut } from 'services/dokumenty/prikazy';

import datovaSchrankaValidation from 'utils/validations/datovaSchrankaValidation';
import icoNidValidation from 'utils/validations/icoNidValidation';
import isinValidation from 'utils/validations/isinValidation';

import { IJinaOsobaData } from '../../NovyDokument';

interface IVypisJinaOsobaComponent {
    nextStep: () => void;
    zadostID: number;
    jinaOsobaData?: IJinaOsobaData;
}

const VypisJinaOsoba: React.FC<IVypisJinaOsobaComponent> = ({ nextStep, zadostID, jinaOsobaData }) => {
    const { language, dokumenty } = useContext(Context);
    const { confirm } = Modal;
    const { setLevel } = useProfily();
    const [isinListLoading, setIco, dataIsins] = useJinaOsobaIsins();
    const [form] = Form.useForm();
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);
    const [refetch, setRefetch] = useState<boolean>(false);
    const [allowEdit, setAllowEdit] = useState<boolean>(false);
    const [data, setData] = useState<IVypisJina>();
    const isSigned = data?.souhlas;

    useEffect(() => {
        setLoading(true);
        PrikazVypisJinyGet(zadostID)
            .then(resp => {
                setData(resp);
                !dataIsins && setIco(resp.idmb);
                form.setFieldsValue({
                    druh: resp.druh,
                    zpusob: resp.zpusob,
                    souhlas: resp.souhlas,
                    zastava: !!resp.zastava,
                    esm: !!resp.esm,
                    majucet: !!resp.majucet,
                    bankucet: !!resp.bankucet
                });
                setIsChecked(!!resp.souhlas);
            })
            .catch(() => setData({} as IVypisJina))
            .finally(() => setLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID, refetch]);

    const onFinish = (values: any) => {
        setLoading(true);
        const dataToSend = {
            ...(values as any),
            datum: values.datum.format('YYYY-MM-DD'),
            zastava: !!values.zastava,
            esm: !!values.esm,
            majucet: !!values.majucet,
            bankucet: !!values.bankucet,
            ...(allowEdit && {
                souhlas: false
            })
        };
        delete dataToSend.idmbSpolecnost;
        delete dataToSend.adresaSpolecnost;
        delete dataToSend.nazevSpolecnost;
        delete dataToSend.chkbxs;
        PrikazVypisJinyPut(zadostID, dataToSend)
            .then(() => {
                setLevel('dokumenty', {
                    ...dokumenty,
                    selectedZadost: {
                        ...dokumenty?.selectedZadost!,
                        isin: values.isin
                    },
                    jinaOsobaZadostData: dataToSend
                });
                if (!allowEdit) {
                    nextStep();
                } else {
                    setRefetch(pv => !pv);
                    setAllowEdit(false);
                }
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        allowEdit && onFinish(form.getFieldsValue());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowEdit]);

    return (
        <LoadingWrapper loading={loading}>
            {data && jinaOsobaData && (
                <>
                    <FormWrapper outForm={form} onFinish={onFinish}>
                        <Title text="DOK_JINA_IDENTIFIKACE_OSOBA" level={5} />
                        <InputComponent
                            disabled={true}
                            initialValue={jinaOsobaData.osobaData?.spolecnost.idmb}
                            label="IDENTIFIKACNI_CISLO"
                            name="idmbSpolecnost"
                            notRequired={true}
                        />
                        <InputComponent
                            initialValue={jinaOsobaData.osobaData?.spolecnost.nazev}
                            label="PRIKAZ_LIKVIDACE_NAZEV"
                            name="nazevSpolecnost"
                            disabled={true}
                            notRequired={true}
                        />
                        <InputComponent
                            initialValue={`${
                                jinaOsobaData.osobaData?.spolecnost.adma
                            }, ${jinaOsobaData.osobaData?.spolecnost.mema}, ${
                                jinaOsobaData.osobaData?.spolecnost.pscm
                            }, ${getCountryText(jinaOsobaData.osobaData?.spolecnost.kost || '', language)}`}
                            label="LEI_DETAIL_ADRESA_TITLE"
                            name="adresaSpolecnost"
                            disabled={true}
                            notRequired={true}
                        />
                        {/*  <SelectComponent
                            name="isin"
                            labelParagraph={'PRIKAZ_ZNIZENI_ISIN'}
                            defaultValue={data?.isin}
                            data={dataIsins}
                            isFormItem={true}
                        />*/}
                        <Title level={5} text={'PRIKAZ_EMITENT_TITLE'} />
                        <InputComponent
                            initialValue={data?.idmb}
                            label="IDENTIFIKACNI_CISLO"
                            name="idmb"
                            maxLength={30}
                            rules={icoNidValidation()}
                            onBlur={ev => {
                                setIco(ev.target.value);
                            }}
                            disabled={isSigned}
                        />
                        <InputComponent
                            initialValue={data?.nazevemitenta}
                            label="ISIN_IDENTIFIKACE_FIRMA"
                            name="nazevemitenta"
                            disabled={isSigned}
                        />
                        {!dataIsins || dataIsins.length ? (
                            <LoadingWrapper loading={isinListLoading}>
                                <SelectComponent
                                    name="isin"
                                    labelParagraph={'ISIN'}
                                    defaultValue={data?.isin}
                                    data={dataIsins || []}
                                    isFormItem={true}
                                    disabled={isSigned}
                                />
                            </LoadingWrapper>
                        ) : (
                            <InputComponent
                                label="ISIN"
                                name="isin"
                                type="text"
                                maxLength={12}
                                rules={isinValidation()}
                                initialValue={data?.isin}
                                capitalize={true}
                                disabled={isSigned}
                            />
                        )}
                        <DatePickerComponent
                            name="datum"
                            initialValue={data?.datum ? dayjs(data.datum) : undefined}
                            // disabledDate={current => current < dayjs().add(3, 'days')}
                            label="ME_POZADAVKY_DATUM1_POZADOVANE"
                            disabled={isSigned}
                        />
                        <SelectComponent
                            name="druh"
                            labelParagraph={'PRIKAZ_JINAOSOBA_VYPIS_DRUH'}
                            defaultValue={data?.druh}
                            data={[
                                {
                                    labelcz: 'DUVERNE_INFO_TITLE_F45',
                                    value: 'F45'
                                }
                            ]}
                            disabled={isSigned}
                            onChange={() => rerender(pv => !pv)}
                            isFormItem={true}
                        />
                        <SelectComponent
                            name="kategorie"
                            labelParagraph={'PRIKAZ_JINAOSOBA_VYPIS_KATEGORIE'}
                            defaultValue={data?.kategorie}
                            data={[
                                {
                                    labelcz: 'PRIKAZ_JINAOSOBA_VYPIS_KATEGORIE_OBA',
                                    value: 'OBA'
                                },
                                {
                                    labelcz: 'PRIKAZ_JINAOSOBA_VYPIS_KATEGORIE_JEDEN',
                                    value: 'JEDEN'
                                }
                            ]}
                            isFormItem={true}
                            disabled={isSigned}
                        />
                        <Form.Item name="chkbxs">
                            <Row>
                                <Form.Item
                                    style={{
                                        fontWeight: 600
                                    }}
                                    name="zastava"
                                    label={<TranslateText>F45_ZAST</TranslateText>}
                                    valuePropName="checked"
                                >
                                    <Checkbox
                                        style={{
                                            position: 'relative',
                                            top: -2,
                                            left: 4
                                        }}
                                        onChange={() => rerender(pv => !pv)}
                                        disabled={isSigned}
                                    />
                                </Form.Item>
                            </Row>
                            <Row>
                                <Form.Item
                                    style={{
                                        fontWeight: 600
                                    }}
                                    name="esm"
                                    label={<TranslateText>F45_ESM</TranslateText>}
                                    valuePropName="checked"
                                >
                                    <Checkbox
                                        style={{
                                            position: 'relative',
                                            top: -2,
                                            left: 4
                                        }}
                                        onChange={() => rerender(pv => !pv)}
                                        disabled={isSigned}
                                    />
                                </Form.Item>
                            </Row>
                            <Row>
                                <Form.Item
                                    style={{
                                        fontWeight: 600
                                    }}
                                    name="majucet"
                                    label={<TranslateText>F45_MAJUCET</TranslateText>}
                                    valuePropName="checked"
                                >
                                    <Checkbox
                                        style={{
                                            position: 'relative',
                                            top: -2,
                                            left: 4
                                        }}
                                        onChange={() => rerender(pv => !pv)}
                                        disabled={isSigned}
                                    />
                                </Form.Item>
                            </Row>
                            <Row>
                                <Form.Item
                                    style={{
                                        fontWeight: 600
                                    }}
                                    name="bankucet"
                                    label={<TranslateText>F45_BANKUCET</TranslateText>}
                                    valuePropName="checked"
                                >
                                    <Checkbox
                                        style={{
                                            position: 'relative',
                                            top: -2,
                                            left: 4
                                        }}
                                        onChange={() => rerender(pv => !pv)}
                                        disabled={isSigned}
                                    />
                                </Form.Item>
                            </Row>
                        </Form.Item>
                        <SelectComponent
                            name="zpusob"
                            labelParagraph={'PRIKAZ_JINAOSOBA_VYPIS_VYSTUP'}
                            defaultValue={data?.zpusob}
                            data={[
                                {
                                    labelcz: 'PRIKAZ_JINAOSOBA_VYPIS_VYSTUP_DATOVKA',
                                    value: EZpusobPrevzati.DATOVKA
                                }
                            ]}
                            isFormItem={true}
                            disabled={isSigned}
                            onChange={() => rerender(pv => !pv)}
                        />
                        {form.getFieldValue('zpusob') === EZpusobPrevzati.DATOVKA && (
                            <InputComponent
                                initialValue={data?.datovka}
                                label="DATOVA_SCHRANKA"
                                name="datovka"
                                disabled={isSigned}
                                rules={datovaSchrankaValidation()}
                            />
                        )}
                        <PrilohyTable zadostID={zadostID} required={true} dokJiny={true} />
                        <Title level={5} text="PODMINKY_PODANI_ZADOSTI" />
                        <Paragraph
                            text={'PRIKAZ_JINA_VYPIS_TEXT'}
                            style={{
                                display: 'block',
                                marginTop: 10
                            }}
                        />
                        <Paragraph
                            text={'PRIKAZ_JINA_VYPIS_TEXT2'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />
                        <Paragraph
                            text={'PRIKAZ_JINA_VYPIS_TEXT3'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />

                        <Row
                            style={{
                                marginTop: 26
                            }}
                        >
                            <CheckboxComponent
                                name="souhlas"
                                checkboxText={<TranslateText>POTVRZENI_CHECKBOX</TranslateText>}
                                onChange={() => setIsChecked(pv => !pv)}
                                checked={isChecked}
                                value={isChecked}
                                disabled={isSigned}
                            />
                        </Row>
                        <Row className="button-row">
                            <Col>
                                <ButtonComponent
                                    buttontext="POKRACOVAT"
                                    id="isinPokracovatBtn"
                                    disabled={!isChecked}
                                    htmlType="submit"
                                />
                            </Col>
                        </Row>
                    </FormWrapper>
                </>
            )}
            {isSigned && (
                <Button
                    style={{
                        top: '30%',
                        right: 10,
                        position: 'fixed',
                        zIndex: 999
                    }}
                    type="primary"
                    onClick={() =>
                        confirm({
                            title: <TranslateText>DOKUMENTY_OSOBY_ZMENIT_JINA</TranslateText>,
                            cancelText: <TranslateText>NE</TranslateText>,
                            okText: <TranslateText>ANO</TranslateText>,
                            onOk() {
                                setAllowEdit(true);
                            }
                        })
                    }
                >
                    <TranslateText>POVOLIT_EDITACI</TranslateText>
                </Button>
            )}
        </LoadingWrapper>
    );
};

export default VypisJinaOsoba;
